import type {AgencyConfig, BlackoutSettings} from 'src/types/agency';

import {useSelector} from 'react-redux';

import {
  selectAgencyConfig,
  selectEnableVoiceflowPage,
} from 'src/selectors/agency';
import {DEFAULT_NO_BLACKOUT_VALUE} from 'src/utils/blackout-window';


type UseAgencyConfig = ((
  key: string,
) => undefined | boolean | number | string) &
  (() => AgencyConfig);

/**
 * NOTE (kyle): the preferred way to call this hook is like this
 * ```
 * useAgencyConfig().myConfigFlag;
 * ```
 * This ensures you get the correct flag Flow type.
 */
// @ts-expect-error - TS2322 - Type '(key: string) => any' is not assignable to type 'UseAgencyConfig'.
const useAgencyConfig: UseAgencyConfig = function useAgencyConfig(key) {
  const agencyConfig = useSelector(selectAgencyConfig);
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'AgencyConfig'.
  return key ? agencyConfig[key] : agencyConfig;
};
export default useAgencyConfig;

export function useMultiEntityEnabled(): true {
  return true;
}

export function useChatbotEnabled(): boolean {
  return useAgencyConfig().enableChatbot;
}

export function useVoiceAiEnabled(): boolean {
  return useAgencyConfig().voiceAiEnabled;
}

export function useSchedulerEnabled(): boolean {
  return useAgencyConfig().enableSchedulerProduct;
}

export function useDefaultPhoneCountry(): string {
  return useAgencyConfig().defaultPhoneCountry;
}

export function useChatbotSourcing(): boolean {
  const configs = useAgencyConfig();
  return (
    configs.chatbotT2AEnabled ||
    configs.chatbotSourcingWidgetEnabled ||
    configs.chatbotSourcingLinkEnabled
  );
}

export function useVoiceAiMarketingPageEnabled(): boolean {
  return Boolean(useAgencyConfig('voiceAiWelcomePage'));
}

export function useChatbotOutbound(): boolean {
  return useAgencyConfig().chatbotOutboundEnabled;
}

export function useChatbotSourcingWidget(): boolean {
  return useAgencyConfig().chatbotSourcingWidgetEnabled;
}

export function useChatbotT2A(): boolean {
  return useAgencyConfig().chatbotT2AEnabled;
}

export function useJobMatchEnabledForEngage(): boolean {
  return useAgencyConfig().enableJobMatchForEngage;
}

export function useJobMatchEnabledForAnyProduct(): boolean {
  const config = useAgencyConfig();

  return (
    config.enableJobMatchForEngage ||
    config.enableJobMatchForMessaging ||
    config.enableJobMatchForOutboundChatbot ||
    config.enableJobMatchForInboundChatbot
  );
}

export function useChatbotSourcingLink(): boolean {
  return useAgencyConfig().chatbotSourcingLinkEnabled;
}

export function useSenseEvents(): boolean {
  return useAgencyConfig().enableSenseHiringEvents;
}

export function useBlackoutSettings(): BlackoutSettings {
  const {smsBlackoutSettings, emailBlackoutSettings} = useAgencyConfig(); //both are json strings
  return {
    smsBlackoutSettings: JSON.parse(smsBlackoutSettings),
    emailBlackoutSettings: JSON.parse(emailBlackoutSettings),
  };
}
export function useBlackoutSettingsOverlap({
  time,
  type,
}: {
  time: string | null | undefined;
  type: 'sms' | 'email';
}): {
  isOverlapping: boolean;
} {
  const result = {isOverlapping: false} as const;
  const {smsBlackoutSettings, emailBlackoutSettings} = useBlackoutSettings(); //both are json strings

  const blackoutSettings =
    type === 'sms' ? smsBlackoutSettings : emailBlackoutSettings;
  const {start_time, end_time} = blackoutSettings;
  //if any of start time and end time is no_blackout, then return false
  if ([start_time, end_time].includes(DEFAULT_NO_BLACKOUT_VALUE)) {
    return result;
  }
  if (time != null) {
    // @ts-expect-error - TS2540 - Cannot assign to 'isOverlapping' because it is a read-only property.
    result.isOverlapping =
      end_time > start_time
        ? time >= start_time && time <= end_time
        : time >= start_time || time <= end_time;
  }
  return result;
}

export function useAtsFieldTriggeredWritebackEnabled(): boolean {
  return useAgencyConfig().atsFieldTriggeredWritebackEnabled;
}

export function useEnableSenseAi(): boolean {
  return useAgencyConfig().enableSenseAi ?? false;
}
export function useSenseAi(): boolean {
  const agencyConfig = useSelector(selectAgencyConfig);
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type '"useSenseAi"' can't be used to index type 'AgencyConfig'.
  return agencyConfig['useSenseAi'] ?? false;
}

export function useDisableNewMessageCompose(): boolean {
  const agencyConfig = useSelector(selectAgencyConfig);
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type '"disableNewMessageCompose"' can't be used to index type 'AgencyConfig'.
  return agencyConfig['disableNewMessageCompose'] ?? false;
}

export function useAtsWritebackToManyFieldsHandlingMode():
  | 'REPLACE'
  | 'APPEND' {
  return useAgencyConfig().atsWritebackToManyFieldsHandlingMode;
}
export function useCustomDailyFrequencyEnabled(): boolean {
  return useAgencyConfig().customDailyFrequencyEnabled ?? false;
}
export function useEnableVoiceflowPage(): boolean {
  return useSelector(selectEnableVoiceflowPage);
}
export function useAutomationSurvey(): boolean {
  return useAgencyConfig().aepSurveyV2Enabled ?? false;
}
export function useAutomationLinkTracking(): boolean {
  return useAgencyConfig().aepLinkTrackingV2Enabled ?? false;
}
export function useAutomationWorkflowTemplateEnabled(): boolean {
  return useAgencyConfig().enableWorkflowTemplate ?? false;
}

export function useTrmLeadEntityToSourceCandidate(): boolean {
  return useAgencyConfig().trmEnableLeadEntityToSourceCandidate ?? false;
}

export function useDefaultKanbanViewType(): boolean {
  return useAgencyConfig().kanbanBoardDefaultView ?? false;
}

export function useShowJourneyButton(): boolean {
  return useAgencyConfig().journeyConfig?.showJourneyButton ?? true;
}

export function useEnableJ2TimelineDebugger(): boolean {
  return useAgencyConfig().enableAepTimelineDebugger ?? false;
}

export function useAiRecruiterV2(): boolean {
  return useAgencyConfig().aiRecruiterV2Enabled ?? false;
}
